/**
 * Effect applicator for the Legendary Arena rule execution pipeline.
 *
 * applyRuleEffects applies RuleEffect[] to G deterministically using
 * for...of. Each effect type has a dedicated handler. Unknown effect types
 * push a warning to G.messages — they never throw.
 *
 * No boardgame.io imports. No .reduce(). No throw.
 */
import { discardFromHand } from '../moves/discardFromHand.js';
import { drawCardsIntoHand } from '../moves/drawCards.logic.js';
import { pushLog } from '../log/logPush.js';
/**
 * Applies a queueMessage effect by pushing the message to G.messages.
 *
 * @param gameState - The game state to mutate.
 * @param effect - The queueMessage effect to apply.
 */
function applyQueueMessage(gameState, effect) {
    // why: default to `neutral` so the ~dozen existing queueMessage callers stay
    // byte-identical; only Scheme-adversity messages pass `threat` (WP-643-follow-up).
    pushLog(gameState, effect.message, effect.outcome ?? 'neutral');
}
/**
 * Applies a modifyCounter effect by adding delta to the named counter.
 *
 * @param gameState - The game state to mutate.
 * @param effect - The modifyCounter effect to apply.
 */
function applyModifyCounter(gameState, effect) {
    gameState.counters[effect.counter] =
        (gameState.counters[effect.counter] ?? 0) + effect.delta;
}
/**
 * Applies a drawCards effect by moving cards from deck to hand.
 *
 * Delegates the deck-to-hand fill (with discard reshuffle on exhaustion)
 * to the shared drawCardsIntoHand primitive — the same primitive used by
 * the drawCards move and the play-phase onBegin auto-draw, so there is one
 * draw mechanism across the engine. This is the hero `draw` keyword path;
 * it deliberately does NOT consult G.hasDrawnThisTurn, so a card-effect
 * draw is never blocked by the move's once-per-turn guard. The caller owns
 * the player-not-found diagnostic; the helper owns the draw.
 *
 * @param gameState - The game state to mutate.
 * @param effect - The drawCards effect to apply.
 * @param ctx - Context providing deterministic shuffle.
 */
function applyDrawCards(gameState, effect, ctx) {
    const playerZones = gameState.playerZones[effect.playerId];
    if (!playerZones) {
        pushLog(gameState, `Draw effect skipped: player "${effect.playerId}" not found in playerZones.`);
        return;
    }
    drawCardsIntoHand(playerZones, effect.count, ctx);
}
/**
 * Applies a discardHand effect by moving all hand cards to discard.
 *
 * @param gameState - The game state to mutate.
 * @param effect - The discardHand effect to apply.
 */
function applyDiscardHand(gameState, effect) {
    const playerZones = gameState.playerZones[effect.playerId];
    if (!playerZones) {
        pushLog(gameState, `Discard hand effect skipped: player "${effect.playerId}" not found in playerZones.`);
        return;
    }
    // why: WP-498 / D-24301 — route each hand card through the single forced-discard
    // chokepoint so a return-on-discard card discarded by this rule effect offers to
    // return. Snapshot the hand first (discardFromHand mutates it in place).
    for (const cardId of [...playerZones.hand]) {
        discardFromHand(gameState, effect.playerId, cardId);
    }
}
/**
 * Applies an array of RuleEffect descriptions to the game state.
 *
 * Effects are applied in sequence using for...of. Each effect type is
 * handled by a dedicated function. Unknown effect types push a warning
 * to G.messages and continue — they never throw.
 *
 * @param gameState - The game state to mutate.
 * @param ctx - Context providing deterministic shuffle (for drawCards).
 * @param effects - Array of RuleEffect descriptions to apply in order.
 * @returns The mutated game state (same reference as input).
 */
export function applyRuleEffects(gameState, ctx, effects) {
    for (const effect of effects) {
        if (effect.type === 'queueMessage') {
            applyQueueMessage(gameState, effect);
        }
        else if (effect.type === 'modifyCounter') {
            applyModifyCounter(gameState, effect);
        }
        else if (effect.type === 'drawCards') {
            applyDrawCards(gameState, effect, ctx);
        }
        else if (effect.type === 'discardHand') {
            applyDiscardHand(gameState, effect);
        }
        else {
            // why: Unknown effect types are handled gracefully rather than thrown.
            // New effect types added in later packets should fail gracefully in
            // older runtime versions rather than crashing the game.
            const unknownType = effect.type;
            pushLog(gameState, `Unknown rule effect type "${unknownType}" was skipped.`);
        }
    }
    return gameState;
}
//# sourceMappingURL=ruleRuntime.effects.js.map